import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Typography, Card, Descriptions, Tag, Space, Button, Spin, Alert } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import { RecordComments } from '../components/RecordCommentsDrawer';
import ClientTicketsCard from '../components/ClientTicketsCard';
import { ContactModal, STATUS_COLOR } from '../components/ClientForms';
import { useBackFallback, usePageLabel } from '../components/BackChip';

// Admin > Clients > a contact: the contact's details, their clients,
// campaigns and teams, their Help Desk tickets, and their comment log. Edit
// (Clients Edit and up) opens the same contact popup as the list. There is no
// delete: a contact is retired by setting it Inactive. What is shown follows
// the lists: below Edit Manager, only the caller's Visible Campaigns.

export default function ClientContactPage() {
  const { id } = useParams();
  const contactId = Number(id);
  const currentUser = useContext(UserContext);
  // The Back chip (Layout): back where you came from, else this list.
  useBackFallback('/admin/clients', 'Clients');
  const canEdit = (currentUser?.CanEditClients ?? 0) >= 2;

  const [contact, setContact]   = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(contact ? `Client Contact: ${contact.FirstName} ${contact.LastName}` : null);
  const [clients, setClients]   = useState([]);
  const [options, setOptions]   = useState({ campaigns: [], teams: [], queues: [] });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  // The contacts list already carries everything this page shows, limited the
  // same way, so it is read from there.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [co, cl, op] = await Promise.all([
        api.get('/api/client-contacts'),
        api.get('/api/clients'),
        api.get('/api/clients/options'),
      ]);
      const k = (co.data || []).find((x) => x.ID === contactId);
      setContact(k || null);
      setClients(cl.data || []);
      setOptions(op.data || { campaigns: [], teams: [], queues: [] });
      setError(k ? null : 'Contact not found.');
    } catch {
      setError('Failed to load the contact.');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load]);

  const campaignById = useMemo(() => Object.fromEntries((options.campaigns || []).map((c) => [c.ID, c.Name])), [options]);
  const teamById     = useMemo(() => Object.fromEntries((options.teams || []).map((t) => [t.ID, t.Name])), [options]);
  const clientById   = useMemo(() => Object.fromEntries(clients.map((c) => [c.ID, c.Name])), [clients]);

  if (loading && !contact) return <Spin />;
  if (error) return <Alert type="error" showIcon message={error} />;
  if (!contact) return null;

  const name = `${contact.FirstName} ${contact.LastName}`;
  const tags = (ids, byId) => ((ids || []).length
    ? <Space size={[0, 4]} wrap>{ids.map((x) => <Tag key={x}>{byId[x] || `#${x}`}</Tag>)}</Space>
    : '—');

  const commentRecord = {
    kind: 'client-contacts',
    id: contact.ID,
    name,
    campaigns: (contact.CampaignIDs || []).map((c) => ({ value: c, label: campaignById[c] || `#${c}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  };

  return (
    <div>
      <Space align="center" style={{ marginBottom: 16 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>{name}</Typography.Title>
        {contact.Status && <Tag color={STATUS_COLOR[contact.Status]}>{contact.Status}</Tag>}
      </Space>

      <Card
        title="Contact"
        size="small"
        style={{ marginBottom: 16 }}
        extra={canEdit && <Button size="small" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>Edit</Button>}
      >
        <Descriptions size="small" bordered column={{ xs: 1, md: 2, lg: 3 }}>
          <Descriptions.Item label="Name">{name}</Descriptions.Item>
          <Descriptions.Item label="Title">{contact.Title || '—'}</Descriptions.Item>
          <Descriptions.Item label="Status">{contact.Status || '—'}</Descriptions.Item>
          <Descriptions.Item label="Email">{contact.Email ? <a href={`mailto:${contact.Email}`}>{contact.Email}</a> : '—'}</Descriptions.Item>
          <Descriptions.Item label="Phone">{contact.Phone || '—'}</Descriptions.Item>
          <Descriptions.Item label="Cell">{contact.Cell || '—'}</Descriptions.Item>
          <Descriptions.Item label="Clients" span={3}>
            {(contact.ClientIDs || []).length
              ? <Space size={[0, 4]} wrap>{contact.ClientIDs.map((c) => (
                  <Tag key={c}><Link to={`/admin/clients/${c}`}>{clientById[c] || `#${c}`}</Link></Tag>))}</Space>
              : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Campaigns" span={3}>{tags(contact.CampaignIDs, campaignById)}</Descriptions.Item>
          <Descriptions.Item label="Teams" span={3}>{tags(contact.TeamIDs, teamById)}</Descriptions.Item>
          <Descriptions.Item label="Created">{formatTS(contact.CreatedAt)}</Descriptions.Item>
          <Descriptions.Item label="Updated">{formatTS(contact.UpdatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <ClientTicketsCard params={{ client_contact_ids: contact.ID }} showContact={false} />

      <Card title="Comments" size="small">
        <RecordComments record={commentRecord} canAdd={canEdit} onChanged={() => {}} />
      </Card>

      <ContactModal
        open={editOpen}
        contact={contact}
        clients={clients}
        options={options}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />
    </div>
  );
}
